/**
 * Tiny WebGL fragment-shader runner.
 * Renders a full-screen quad, pipes mouse/click/time uniforms, eases the cursor.
 * Owns its RAF loop and listeners — callers must invoke destroy() for cleanup.
 */

const VERT = `
    attribute vec2 a_pos;
    void main(){ gl_Position = vec4(a_pos, 0.0, 1.0); }
`;

function compile(gl, type, src) {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        const log = gl.getShaderInfoLog(s);
        gl.deleteShader(s);
        throw new Error('shader compile error: ' + log);
    }
    return s;
}

function program(gl, vs, fs) {
    const p = gl.createProgram();
    gl.attachShader(p, compile(gl, gl.VERTEX_SHADER, vs));
    gl.attachShader(p, compile(gl, gl.FRAGMENT_SHADER, fs));
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
        const log = gl.getProgramInfoLog(p);
        gl.deleteProgram(p);
        throw new Error('program link error: ' + log);
    }
    return p;
}

export class ShaderRenderer {
    constructor(canvas) {
        this.canvas = canvas;
        const gl = canvas.getContext('webgl', { antialias: false, premultipliedAlpha: false });
        if (!gl) throw new Error('WebGL not available');
        this.gl = gl;

        const buf = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        gl.bufferData(
            gl.ARRAY_BUFFER,
            new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
            gl.STATIC_DRAW,
        );

        this.mouse = [0.5, 0.5];
        this.mouseTarget = [0.5, 0.5];
        this.click = [0.5, 0.5];
        this.clickTime = -1000;
        this.startTime = performance.now() / 1000;
        this.prog = null;
        this.uniforms = {};
        this._raf = null;

        this._onResize = () => this._resize();
        this._onPointerMove = (e) => {
            const r = canvas.getBoundingClientRect();
            this.mouseTarget = [(e.clientX - r.left) / r.width, 1.0 - (e.clientY - r.top) / r.height];
        };
        this._onPointerDown = (e) => {
            const r = canvas.getBoundingClientRect();
            this.click = [(e.clientX - r.left) / r.width, 1.0 - (e.clientY - r.top) / r.height];
            this.clickTime = performance.now() / 1000 - this.startTime;
        };

        window.addEventListener('resize', this._onResize);
        canvas.addEventListener('pointermove', this._onPointerMove);
        canvas.addEventListener('pointerdown', this._onPointerDown);

        this._resize();
    }

    _resize() {
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const w = Math.floor(this.canvas.clientWidth * dpr);
        const h = Math.floor(this.canvas.clientHeight * dpr);
        if (this.canvas.width !== w || this.canvas.height !== h) {
            this.canvas.width = w;
            this.canvas.height = h;
        }
        this.gl.viewport(0, 0, w, h);
    }

    setShader(fragSrc) {
        const gl = this.gl;
        if (this.prog) gl.deleteProgram(this.prog);
        this.prog = program(gl, VERT, fragSrc);
        gl.useProgram(this.prog);
        const loc = gl.getAttribLocation(this.prog, 'a_pos');
        gl.enableVertexAttribArray(loc);
        gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
        this.uniforms = {
            u_res: gl.getUniformLocation(this.prog, 'u_res'),
            u_time: gl.getUniformLocation(this.prog, 'u_time'),
            u_mouse: gl.getUniformLocation(this.prog, 'u_mouse'),
            u_click: gl.getUniformLocation(this.prog, 'u_click'),
            u_clickTime: gl.getUniformLocation(this.prog, 'u_clickTime'),
        };
    }

    frame() {
        this._resize();
        this.mouse[0] += (this.mouseTarget[0] - this.mouse[0]) * 0.12;
        this.mouse[1] += (this.mouseTarget[1] - this.mouse[1]) * 0.12;

        const gl = this.gl;
        if (!this.prog) return;
        const t = performance.now() / 1000 - this.startTime;
        gl.uniform2f(this.uniforms.u_res, this.canvas.width, this.canvas.height);
        gl.uniform1f(this.uniforms.u_time, t);
        gl.uniform2f(this.uniforms.u_mouse, this.mouse[0], this.mouse[1]);
        gl.uniform2f(this.uniforms.u_click, this.click[0], this.click[1]);
        gl.uniform1f(this.uniforms.u_clickTime, this.clickTime);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
    }

    start() {
        if (this._raf !== null) return;
        const loop = () => {
            this.frame();
            this._raf = requestAnimationFrame(loop);
        };
        loop();
    }

    stop() {
        if (this._raf !== null) {
            cancelAnimationFrame(this._raf);
            this._raf = null;
        }
    }

    destroy() {
        this.stop();
        window.removeEventListener('resize', this._onResize);
        this.canvas.removeEventListener('pointermove', this._onPointerMove);
        this.canvas.removeEventListener('pointerdown', this._onPointerDown);
        if (this.prog) {
            this.gl.deleteProgram(this.prog);
            this.prog = null;
        }
        this.gl.getExtension('WEBGL_lose_context')?.loseContext();
    }
}
